import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { brand } from "@/lib/brand";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Eye, EyeOff, ArrowRight, UserPlus, Check, Sparkles } from "lucide-react";
import type { AppRole } from "@/lib/auth-context";
import {
  PaymentsIcon, AcademicIcon, ComplianceIcon, InsightsIcon,
  TransportIcon, HealthIcon, IdentityIcon, CommerceIcon,
} from "@/components/icons/breccs-icons";

const VALID_ROLES: AppRole[] = ["parent", "student", "school_admin", "finance_officer", "merchant", "superadmin"];

type PortalDef = {
  key: AppRole;
  label: string;
  icon: typeof PaymentsIcon;
  desc: string;
  tag: string;
  admin?: boolean;
};

const PORTALS: PortalDef[] = [
  { key: "parent",          label: "Parent",         icon: PaymentsIcon,  desc: "Fees, transport, health, messaging", tag: "For families" },
  { key: "school_admin",    label: "Admin",           icon: AcademicIcon,  desc: "Run your school from one console", tag: "For schools", admin: true },
  { key: "finance_officer", label: "Finance Officer", icon: InsightsIcon,  desc: "School finances, payments & reconciliation", tag: "For finance teams", admin: true },
  { key: "merchant",        label: "Merchant",        icon: CommerceIcon,   desc: "Campus commerce & settlements", tag: "For vendors" },
  { key: "superadmin",      label: "Super Admin",     icon: ComplianceIcon, desc: "Platform-wide oversight & control", tag: "For platform ops", admin: true },
];

const FS = "'Montserrat', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

// Network nodes for the admin command-center visualization
const NETWORK_NODES = [
  { x: 15, y: 20, r: 4, delay: 0 },
  { x: 50, y: 12, r: 6, delay: 0.3 },
  { x: 82, y: 25, r: 4, delay: 0.6 },
  { x: 25, y: 50, r: 5, delay: 0.9 },
  { x: 60, y: 45, r: 7, delay: 1.2 },
  { x: 88, y: 55, r: 4, delay: 1.5 },
  { x: 18, y: 75, r: 5, delay: 1.8 },
  { x: 48, y: 80, r: 4, delay: 2.1 },
  { x: 75, y: 72, r: 6, delay: 2.4 },
  { x: 38, y: 30, r: 3, delay: 2.7 },
  { x: 70, y: 38, r: 3, delay: 3.0 },
];

const EDGES: [number, number][] = [
  [0, 1], [1, 2], [0, 3], [1, 4], [2, 5], [3, 4], [4, 5], [3, 6], [4, 7], [5, 8], [6, 7], [7, 8], [1, 9], [4, 10],
];

function AdminLeftPanel({ role }: { role: AppRole }) {
  const isSuper = role === "superadmin";
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden lg:flex lg:w-[54%]" style={{ background: "#082F49" }}>
      <div className="pointer-events-none absolute inset-0" style={{
        background: `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.15) 0%, transparent 50%), radial-gradient(ellipse 60% 50% at 90% 100%, rgba(232,55,26,0.06) 0%, transparent 50%)`,
      }} />

      <svg className="pointer-events-none absolute inset-0 h-full w-full" preserveAspectRatio="none">
        {EDGES.map(([a, b], i) => (
          <motion.line
            key={i}
            x1={`${NETWORK_NODES[a].x}%`} y1={`${NETWORK_NODES[a].y}%`}
            x2={`${NETWORK_NODES[b].x}%`} y2={`${NETWORK_NODES[b].y}%`}
            stroke="rgba(14,165,233,0.15)" strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: [0, 0.8, 0.3] }}
            transition={{ duration: 2, delay: i * 0.08, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          />
        ))}
        {NETWORK_NODES.map((n, i) => (
          <motion.circle
            key={i}
            cx={`${n.x}%`} cy={`${n.y}%`} r={n.r}
            fill="rgba(14,165,233,0.4)"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1, 0.7, 1], opacity: [0, 0.8, 0.4, 0.8] }}
            transition={{ duration: 3, delay: n.delay, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          />
        ))}
      </svg>

      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(14,165,233,0.5), transparent)" }} />

      <div className="relative z-10 flex h-full flex-col justify-between p-12 xl:p-16">
        <Link to="/" className="flex items-center gap-3">
          <img src={brand.logoUrl} alt="BRECCS Pay" className="h-10 w-10 rounded-[10px] object-contain" />
          <div className="leading-tight">
            <div className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: FS }}>{brand.name}</div>
            <div className="text-[8px] font-bold uppercase tracking-[0.25em]" style={{ color: "rgba(14,165,233,0.8)" }}>{brand.tagline}</div>
          </div>
        </Link>

        <div className="my-auto max-w-md">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="mb-6 flex items-center gap-3">
              <div className="h-px w-10" style={{ background: "rgba(14,165,233,0.6)" }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: "rgba(14,165,233,0.9)", fontFamily: FS }}>
                {isSuper ? "Platform Command" : "School Operations"}
              </span>
            </div>

            <h1 style={{ fontFamily: FS, color: "white", fontSize: "clamp(2.8rem, 5vw, 4.5rem)", fontWeight: 800, lineHeight: 0.92, letterSpacing: "-0.03em" }}>
              {isSuper ? "Every school." : "Your school,"}
            </h1>
            <h1 style={{ fontFamily: FS, color: "#0EA5E9", fontSize: "clamp(2.8rem, 5vw, 4.5rem)", fontWeight: 800, lineHeight: 0.92, letterSpacing: "-0.03em" }}>
              {isSuper ? "One console." : "fully connected."}
            </h1>

            <p className="mt-6 max-w-sm text-base leading-relaxed" style={{ fontFamily: FS, color: "rgba(255,255,255,0.55)" }}>
              {isSuper
                ? "Oversee every tenant, transaction, and institution across the entire BRECCS Pay network — in real time."
                : "Payments, academics, transport, health, and compliance — unified on one ledger, one identity, one dashboard."}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25, ease: [0.16, 1, 0.3, 1] }} className="mt-10 flex flex-wrap gap-2">
            {(isSuper
              ? [IdentityIcon, PaymentsIcon, AcademicIcon, TransportIcon, HealthIcon, ComplianceIcon, CommerceIcon, InsightsIcon]
              : [PaymentsIcon, AcademicIcon, TransportIcon, HealthIcon, ComplianceIcon, InsightsIcon]
            ).map((Icon, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + i * 0.06, duration: 0.4 }}
                className="flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(14,165,233,0.15)" }}
              >
                <Icon size={18} style={{ color: "rgba(14,165,233,0.7)" }} />
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.3)", fontFamily: FS }}>
            {brand.company} · {brand.city}, {brand.country}
          </span>
          <div className="flex items-center gap-1.5">
            <motion.div className="h-1.5 w-1.5 rounded-full bg-green-400" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2, repeat: Infinity }} />
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.3)", fontFamily: FS }}>All systems operational</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsumerLeftPanel() {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden lg:flex lg:w-[54%]" style={{ background: "var(--brand-forest)" }}>
      <img
        src="/images/woman-with-giant-pencil.jpg"
        alt="Student celebrating education"
        className="absolute inset-0 h-full w-full object-cover opacity-60"
      />
      <div className="pointer-events-none absolute inset-0" style={{
        background: `linear-gradient(135deg, rgba(8,47,73,0.82) 0%, rgba(8,47,73,0.62) 42%, rgba(14,116,144,0.28) 100%), radial-gradient(ellipse 70% 50% at 20% 15%, rgba(14,165,233,0.22) 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 80% 85%, rgba(201,207,210,0.10) 0%, transparent 55%)`,
      }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `radial-gradient(circle, var(--brand-silver) 1px, transparent 1px)`, backgroundSize: "32px 32px" }} />
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, var(--brand-green), var(--brand-emerald), var(--brand-green))" }} />

      <div className="relative z-10 flex h-full flex-col justify-between p-12 xl:p-16">
        <Link to="/" className="flex items-center gap-3">
          <img src={brand.logoUrl} alt="BRECCS Pay logo" className="h-10 w-10 rounded-[10px] object-contain" />
          <div className="leading-tight">
            <div className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: FS }}>{brand.name}</div>
            <div className="text-[8px] font-bold uppercase tracking-[0.25em]" style={{ color: "var(--brand-emerald)" }}>{brand.tagline}</div>
          </div>
        </Link>

        <div className="my-auto max-w-md">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="mb-6 flex items-center gap-3">
              <div className="h-px w-10" style={{ background: "var(--brand-emerald)" }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: "var(--brand-emerald)", fontFamily: FS }}>School Finance OS</span>
            </div>

            <h1 style={{ fontFamily: FS, color: "white", fontSize: "clamp(3rem, 6vw, 5.5rem)", fontWeight: 800, lineHeight: 0.88, letterSpacing: "-0.02em" }}>ONE TAP.</h1>
            <h1 style={{ fontFamily: FS, color: "var(--brand-emerald)", fontSize: "clamp(3rem, 6vw, 5.5rem)", fontWeight: 800, lineHeight: 0.88, letterSpacing: "-0.02em" }}>Every Bill.</h1>

            <p className="mt-6 max-w-sm text-base leading-relaxed" style={{ fontFamily: FS, color: "rgba(255,255,255,0.78)" }}>
              Pay school fees, transport, meals and uniforms in a single Mobile Money tap — automatic, instant, reconciled.
            </p>
            <div className="mt-8 flex items-center gap-3 text-xs font-medium text-white/60">
              <div className="flex -space-x-2">
                <span className="h-7 w-7 rounded-full border-2 border-[#0A3B23] bg-[#D9A441]" />
                <span className="h-7 w-7 rounded-full border-2 border-[#0A3B23] bg-[#7BAE8A]" />
                <span className="h-7 w-7 rounded-full border-2 border-[#0A3B23] bg-[#C78D69]" />
              </div>
              Trusted by families across Zimbabwe
            </div>
          </motion.div>
        </div>

        <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.4)", fontFamily: FS }}>
          {brand.company} · {brand.city}, {brand.country}
        </div>
      </div>
    </div>
  );
}

export function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<{ school_name: string; student_name: string } | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const [role, setRole] = useState<AppRole>(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search).get("portal") as AppRole | null;
      if (p && VALID_ROLES.includes(p)) return p;
    }
    return "parent";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const isAdminPortal = useMemo(() => role === "school_admin" || role === "superadmin" || role === "finance_officer", [role]);
  const activePortal = PORTALS.find(p => p.key === role) ?? PORTALS[0];

  useEffect(() => {
    if (typeof window === "undefined") return;
    const code = new URLSearchParams(window.location.search).get("invite");
    if (!code) return;
    setInviteCode(code);
    setRole("parent");
    setMode("signup");
    (async () => {
      const { data } = await supabase
        .from("school_invitations")
        .select("full_name, school_id, schools(name)")
        .eq("invite_code", code)
        .maybeSingle() as any;
      if (data) {
        setInviteInfo({
          school_name: (data as any).schools?.name ?? "your school",
          student_name: (data as any).full_name ?? "your child",
        });
      }
    })();
  }, []);

  async function redeemInvite() {
    if (!inviteCode) return;
    setRedeeming(true);
    try {
      const { data, error } = await supabase.rpc("redeem_school_invitation", { _code: inviteCode }) as any;
      if (error) throw error;
      const result = data as { ok: boolean; school_name?: string; student?: { full_name?: string } };
      toast.success(
        result?.student?.full_name
          ? `${result.student.full_name} is now linked to your account.`
          : "You're now linked to your school."
      );
    } catch (err: any) {
      toast.error(err.message ?? "Could not accept invitation. The code may be invalid or expired.");
    } finally {
      setRedeeming(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email, password,
          options: { data: { full_name: fullName, role } },
        });
        if (error) throw error;
        toast.success("Account created — welcome.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back.");
      }
      if (inviteCode) {
        await redeemInvite();
      }
      window.location.assign("/app");
    } catch (err: any) {
      toast.error(err.message ?? "Authentication failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden" style={{ background: "var(--bg-base)", fontFamily: FS }}>
      <AnimatePresence mode="wait">
        {isAdminPortal ? (
          <motion.div key="admin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="contents">
            <AdminLeftPanel role={role} />
          </motion.div>
        ) : (
          <motion.div key="consumer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="contents">
            <ConsumerLeftPanel />
          </motion.div>
        )}
      </AnimatePresence>

      {/* RIGHT FORM PANEL */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-16 sm:px-10 lg:px-14 xl:px-20">
        {/* Mobile brand */}
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <img src={brand.logoUrl} alt="BRECCS Pay logo" className="h-9 w-9 rounded-[10px] object-contain" />
          <div className="text-lg font-bold tracking-tight" style={{ fontFamily: FS, color: "var(--text-primary)" }}>{brand.name}</div>
        </div>

        <div className="w-full max-w-[460px]">
          {/* Headline */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${mode}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="mb-5 flex items-center gap-3">
                <div className="h-px w-8" style={{ background: "var(--brand-forest)" }} />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: "var(--brand-forest)", fontFamily: FS }}>
                  {mode === "signin" ? "Sign In" : "Create Account"}
                </span>
              </div>

              {mode === "signin" ? (
                <>
                  <h2 style={{ fontFamily: FS, color: "var(--text-primary)", fontWeight: 700, fontSize: "2.4rem", lineHeight: 0.95, letterSpacing: "-0.02em" }}>
                    Welcome back.
                  </h2>
                  <p style={{ fontFamily: FS, color: "var(--brand-green)", fontWeight: 600, fontSize: "2.4rem", lineHeight: 0.95, letterSpacing: "-0.02em" }}>
                    Sign in to continue.
                  </p>
                  <p className="mt-3 text-sm" style={{ fontFamily: FS, color: "var(--text-muted)" }}>
                    Enter your credentials to access your dashboard.
                  </p>
                </>
              ) : (
                <>
                  <h2 style={{ fontFamily: FS, color: "var(--text-primary)", fontWeight: 700, fontSize: "2.4rem", lineHeight: 0.95, letterSpacing: "-0.02em" }}>
                    Join the platform.
                  </h2>
                  <p style={{ fontFamily: FS, color: "var(--brand-green)", fontWeight: 600, fontSize: "2.4rem", lineHeight: 0.95, letterSpacing: "-0.02em" }}>
                    Create your account.
                  </p>
                  <p className="mt-3 text-sm" style={{ fontFamily: FS, color: "var(--text-muted)" }}>
                    Choose your role below — we'll tailor your experience from there.
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {/* INVITE BANNER */}
          {inviteCode && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 rounded-xl border p-4"
              style={{ background: "rgba(14,165,233,0.06)", borderColor: "rgba(14,165,233,0.25)" }}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "var(--brand-forest)" }}>
                  <UserPlus className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold" style={{ fontFamily: FS, color: "var(--text-primary)" }}>
                    School invitation
                  </div>
                  <div className="text-xs leading-relaxed" style={{ fontFamily: FS, color: "var(--text-muted)" }}>
                    {inviteInfo
                      ? `${inviteInfo.school_name} has invited you to join as ${inviteInfo.student_name}'s parent.`
                      : "A school has invited you to link your child's account."}
                    {" "}Create an account or sign in to accept.
                  </div>
                  {redeeming && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs" style={{ color: "var(--brand-green)", fontFamily: FS }}>
                      <Loader2 className="h-3 w-3 animate-spin" /> Linking...
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* PORTAL SELECTOR — only visible during signup */}
          <AnimatePresence>
            {mode === "signup" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-6 mb-2 flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" style={{ color: "var(--brand-forest)" }} />
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "var(--brand-forest)", fontFamily: FS }}>
                    Select your role
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {PORTALS.map((p, i) => {
                    const active = role === p.key;
                    return (
                      <motion.button
                        key={p.key}
                        type="button"
                        onClick={() => setRole(p.key)}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 + i * 0.04, duration: 0.3 }}
                        className="group relative flex items-center gap-3 overflow-hidden rounded-2xl border p-3.5 text-left transition-all"
                        style={{
                          borderColor: active ? (p.admin ? "#082F49" : "var(--brand-forest)") : "var(--border)",
                          background: active ? (p.admin ? "rgba(10,31,20,0.05)" : "rgba(14,116,144,0.05)") : "var(--bg-surface)",
                          borderWidth: active ? 2 : 1.5,
                          boxShadow: active ? (p.admin ? "0 4px 16px -6px rgba(10,31,20,0.2)" : "0 4px 16px -6px rgba(14,116,144,0.2)") : "none",
                        }}
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-110"
                          style={{ background: active ? (p.admin ? "#082F49" : "var(--brand-forest)") : "var(--bg-subtle)" }}>
                          <p.icon size={20} style={{ color: active ? "white" : "var(--text-muted)" }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold leading-tight" style={{ color: active ? "var(--text-primary)" : "var(--text-muted)", fontFamily: FS }}>
                              {p.label}
                            </span>
                            {active && (
                              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex h-4 w-4 items-center justify-center rounded-full"
                                style={{ background: p.admin ? "#082F49" : "var(--brand-forest)" }}>
                                <Check className="h-2.5 w-2.5 text-white" />
                              </motion.div>
                            )}
                          </div>
                          <div className="truncate text-[10px] leading-tight" style={{ color: "var(--text-muted)", fontFamily: FS }}>
                            {p.desc}
                          </div>
                        </div>
                        {active && (
                          <motion.div className="absolute bottom-0 left-0 right-0 h-0.5" layoutId="portal-indicator" style={{ background: p.admin ? "#082F49" : "var(--brand-forest)" }} />
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* FORM */}
          <form onSubmit={submit} className="mt-7 space-y-4">
            <AnimatePresence>
              {mode === "signup" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
                  <AuthField label="Full name" id="name" type="text" value={fullName} onChange={setFullName} placeholder="Jane Namusose" required />
                </motion.div>
              )}
            </AnimatePresence>

            <AuthField label="Email address" id="email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" required />

            <div className="relative">
              <AuthField label="Password" id="password" type={showPw ? "text" : "password"} value={password} onChange={setPassword} placeholder="........" required />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-[34px] p-1" style={{ color: "var(--text-muted)" }}>
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {mode === "signin" && (
              <div className="text-right">
                <a href="/reset-password" className="text-xs font-semibold uppercase tracking-wider transition-opacity hover:opacity-70" style={{ color: "var(--brand-forest)", fontFamily: FS }}>
                  Forgot password?
                </a>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="group mt-2 flex w-full items-center justify-center gap-3 py-4 text-sm font-bold uppercase tracking-[0.18em] transition-all hover:brightness-110 disabled:opacity-60"
              style={{
                background: "var(--brand-forest)",
                color: "white", fontFamily: FS,
                border: "none", borderRadius: 14,
                boxShadow: "0 4px 20px -4px rgba(14,116,144,0.25)",
              }}
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Please wait...</>
              ) : (
                <>
                  {mode === "signin" ? "Sign In" : "Create Account"}
                  <span className="flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-bold transition-transform group-hover:translate-x-0.5"
                    style={{ background: "var(--brand-emerald)", color: "white", borderRadius: 8 }}>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </>
              )}
            </button>
          </form>

          {/* Divider + toggle */}
          <div className="mt-7 flex items-center gap-4">
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
            <span className="text-xs" style={{ color: "var(--text-muted)", fontFamily: FS }}>
              {mode === "signin" ? "New here?" : "Have an account?"}
            </span>
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
          </div>

          <button
            type="button"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="mt-3 flex w-full items-center justify-center gap-2 py-3.5 text-sm font-bold uppercase tracking-[0.15em] transition-all hover:opacity-80"
            style={{ background: "var(--bg-surface)", color: "var(--brand-forest)", border: `1.5px solid var(--border)`, fontFamily: FS, borderRadius: 14 }}
          >
            {mode === "signin" ? (
              <><UserPlus className="h-4 w-4" /> Create an Account</>
            ) : (
              "Sign In Instead"
            )}
          </button>

          <p className="mt-7 text-center text-[11px] leading-relaxed" style={{ color: "var(--text-muted)", fontFamily: FS }}>
            Secured by {brand.company} · {brand.city}, {brand.country}<br />
            All transactions are encrypted and audit-logged
          </p>
        </div>
      </div>
    </div>
  );
}

function AuthField({
  label, id, type, value, onChange, placeholder, required,
}: {
  label: string; id: string; type: string;
  value: string; onChange: (v: string) => void;
  placeholder: string; required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "var(--text-primary)", fontFamily: FS }}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-3 text-sm outline-none transition-all"
        style={{ background: "var(--bg-subtle)", border: `1.5px solid var(--border)`, color: "var(--text-primary)", fontFamily: FS, borderRadius: 12 }}
        onFocus={e => {
          e.currentTarget.style.borderColor = "var(--brand-emerald)";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(14,165,233,0.15)";
          e.currentTarget.style.background = "var(--bg-surface)";
        }}
        onBlur={e => {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.background = "var(--bg-subtle)";
        }}
      />
    </div>
  );
}
